"use client";

import { FormEvent, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { FileUp, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { PublicJobQuestion } from "@/components/careers/job-detail-view";
import { cn } from "@/lib/utils";

function fieldClass() {
  return "mt-1.5 w-full rounded-xl border border-border-strong bg-background px-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-accent";
}

function labelClass(extra?: string) {
  return cn(
    "block text-xs font-semibold uppercase tracking-[0.12em] text-foreground",
    extra,
  );
}

type Personal = {
  candidateName: string;
  email: string;
  phone: string;
  location: string;
};

export function JobApplicationForm({
  slug,
  questions,
}: {
  slug: string;
  questions: PublicJobQuestion[];
}) {
  const router = useRouter();
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeReady, setResumeReady] = useState(false);
  const [personal, setPersonal] = useState<Personal>({
    candidateName: "",
    email: "",
    phone: "",
    location: "",
  });
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [checkboxState, setCheckboxState] = useState<Record<string, string[]>>({});

  const questionMeta = useMemo(
    () => questions.map((q) => ({ id: q.id, question: q.question, type: q.type })),
    [questions],
  );

  const setPersonalField = <K extends keyof Personal>(key: K, value: Personal[K]) =>
    setPersonal((prev) => ({ ...prev, [key]: value }));

  const parseResume = async (file: File) => {
    setParsing(true);
    setError("");
    setInfo("");
    try {
      const fd = new FormData();
      fd.set("resume", file);
      fd.set("questions", JSON.stringify(questionMeta));
      const res = await fetch("/api/jobs/parse-resume", { method: "POST", body: fd });
      const data = (await res.json()) as {
        parsed?: Personal & {
          linkedin?: string;
          github?: string;
          portfolio?: string;
          yearsExperience?: string;
          currentCompany?: string;
        };
        questionHints?: Record<string, string>;
        error?: string;
      };

      if (!res.ok) {
        setResumeReady(true);
        setInfo(data.error || "Resume saved. Please fill the remaining fields manually.");
        return;
      }

      if (data.parsed) {
        setPersonal({
          candidateName: data.parsed.candidateName || "",
          email: data.parsed.email || "",
          phone: data.parsed.phone || "",
          location: data.parsed.location || "",
        });
      }
      if (data.questionHints) {
        setAnswers((prev) => ({ ...prev, ...data.questionHints }));
      }
      setResumeReady(true);
      setInfo("We filled details from your resume. Please review and complete any missing fields.");
    } catch {
      setResumeReady(true);
      setInfo("Resume uploaded. We couldn't auto-fill — please complete the form manually.");
    } finally {
      setParsing(false);
    }
  };

  const onResumeChange = async (file: File | null) => {
    setResumeFile(file);
    setResumeReady(false);
    setInfo("");
    if (!file) return;
    await parseResume(file);
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    if (!resumeFile) {
      setError("Please upload your resume first.");
      setSubmitting(false);
      return;
    }

    const fd = new FormData();
    fd.set("candidateName", personal.candidateName);
    fd.set("email", personal.email);
    fd.set("phone", personal.phone);
    fd.set("location", personal.location);
    fd.set("resume", resumeFile);

    for (const q of questions) {
      if (q.type === "checkbox") {
        (checkboxState[q.id] ?? []).forEach((v) => fd.append(`question_${q.id}`, v));
      } else if (q.type === "file") {
        const input = e.currentTarget.elements.namedItem(`question_${q.id}`);
        if (input instanceof HTMLInputElement && input.files?.[0]) {
          fd.set(`question_${q.id}`, input.files[0]);
        }
      } else {
        fd.set(`question_${q.id}`, answers[q.id] ?? "");
      }
    }

    try {
      const res = await fetch(`/api/jobs/${slug}/apply`, { method: "POST", body: fd });
      const data = (await res.json()) as { applicationId?: string; error?: string };
      if (!res.ok) {
        setError(data.error || "Unable to submit application.");
        return;
      }
      router.push(`/careers/apply/success?id=${encodeURIComponent(data.applicationId || "")}`);
    } catch {
      setError("Unable to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5 rounded-[1.5rem] border border-border bg-card/60 p-5 sm:p-6">
      {error ? (
        <p className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger" role="alert">
          {error}
        </p>
      ) : null}
      {info ? (
        <p className="rounded-xl border border-accent/30 bg-accent/10 px-4 py-3 text-sm text-foreground" role="status">
          <Sparkles className="mr-2 inline h-4 w-4 text-accent" />
          {info}
        </p>
      ) : null}

      <section className="rounded-2xl border border-dashed border-border bg-background/50 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-accent">
            <FileUp className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Step 1 · Upload resume</p>
            <p className="mt-1 text-xs text-muted">
              Upload PDF, DOC, or DOCX (max 10 MB). We&apos;ll extract your details and auto-fill the form.
            </p>
            <label className={labelClass("mt-4")}>
              Resume *
              <input
                type="file"
                required={!resumeFile}
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className={cn(
                  fieldClass(),
                  "file:mr-3 file:rounded-full file:border-0 file:bg-accent/10 file:px-3 file:py-1 file:text-xs file:font-medium file:text-foreground",
                )}
                onChange={(e) => void onResumeChange(e.target.files?.[0] ?? null)}
              />
            </label>
            {resumeFile ? (
              <p className="mt-2 text-xs text-muted">
                Selected: <span className="text-foreground">{resumeFile.name}</span>
                {parsing ? (
                  <span className="ml-2 inline-flex items-center gap-1 text-accent">
                    <Loader2 className="h-3 w-3 animate-spin" /> Parsing…
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
        </div>
      </section>

      <fieldset disabled={parsing || (!resumeReady && !resumeFile)} className="space-y-5 disabled:opacity-80">
        <div>
          <p className="text-sm font-semibold text-foreground">Step 2 · Review your details</p>
          <p className="mt-1 text-xs text-muted">Edit anything that looks incorrect before submitting.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className={labelClass("sm:col-span-2")}>
            Full name *
            <input
              name="candidateName"
              required
              value={personal.candidateName}
              onChange={(e) => setPersonalField("candidateName", e.target.value)}
              className={fieldClass()}
              autoComplete="name"
            />
          </label>
          <label className={labelClass()}>
            Email *
            <input
              name="email"
              type="email"
              required
              value={personal.email}
              onChange={(e) => setPersonalField("email", e.target.value)}
              className={fieldClass()}
              autoComplete="email"
            />
          </label>
          <label className={labelClass()}>
            Phone *
            <input
              name="phone"
              required
              value={personal.phone}
              onChange={(e) => setPersonalField("phone", e.target.value)}
              className={fieldClass()}
              autoComplete="tel"
            />
          </label>
          <label className={labelClass("sm:col-span-2")}>
            Current location
            <input
              name="location"
              value={personal.location}
              onChange={(e) => setPersonalField("location", e.target.value)}
              className={fieldClass()}
              placeholder="City, Country"
            />
          </label>
        </div>

        {questions.length ? (
          <div className="space-y-4 border-t border-border pt-5">
            <p className="text-sm font-semibold text-foreground">Additional questions</p>
            {questions.map((q) => (
              <div key={q.id}>
                <label className={labelClass()}>
                  {q.question}
                  {q.required ? " *" : ""}
                </label>
                {q.helpText ? <p className="mt-1 text-xs text-muted">{q.helpText}</p> : null}

                {q.type === "long_text" ? (
                  <textarea
                    name={`question_${q.id}`}
                    required={q.required}
                    placeholder={q.placeholder}
                    value={answers[q.id] ?? ""}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    className={cn(fieldClass(), "min-h-28")}
                  />
                ) : q.type === "dropdown" ? (
                  <select
                    name={`question_${q.id}`}
                    required={q.required}
                    className={fieldClass()}
                    value={answers[q.id] ?? ""}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                  >
                    <option value="" disabled>
                      Select…
                    </option>
                    {q.options.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : q.type === "radio" ? (
                  <div className="mt-2 space-y-2">
                    {q.options.map((opt) => (
                      <label
                        key={opt}
                        className="flex items-center gap-2 text-sm normal-case tracking-normal text-foreground"
                      >
                        <input
                          type="radio"
                          name={`question_${q.id}`}
                          value={opt}
                          required={q.required}
                          checked={(answers[q.id] ?? "") === opt}
                          onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt }))}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                ) : q.type === "checkbox" ? (
                  <div className="mt-2 space-y-2">
                    {q.options.map((opt) => (
                      <label
                        key={opt}
                        className="flex items-center gap-2 text-sm normal-case tracking-normal text-foreground"
                      >
                        <input
                          type="checkbox"
                          checked={(checkboxState[q.id] ?? []).includes(opt)}
                          onChange={(e) => {
                            setCheckboxState((prev) => {
                              const current = new Set(prev[q.id] ?? []);
                              if (e.target.checked) current.add(opt);
                              else current.delete(opt);
                              return { ...prev, [q.id]: [...current] };
                            });
                          }}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                ) : q.type === "file" ? (
                  <input name={`question_${q.id}`} type="file" required={q.required} className={fieldClass()} />
                ) : (
                  <input
                    name={`question_${q.id}`}
                    type={q.type === "number" ? "number" : "text"}
                    required={q.required}
                    placeholder={q.placeholder}
                    value={answers[q.id] ?? ""}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    className={fieldClass()}
                  />
                )}
              </div>
            ))}
          </div>
        ) : null}

        <Button
          type="submit"
          variant="secondary"
          size="lg"
          className="w-full sm:w-auto"
          disabled={submitting || parsing || !resumeFile}
        >
          {submitting ? "Submitting…" : "Submit application"}
        </Button>
      </fieldset>
    </form>
  );
}
